/* ================================================================
   离屏 three.js 渲染器（V2，与 Viewport3D 视觉一致）
   - 复用 boxDesigner/core/textures 的贴图缓存与 normal map
   - 复用 SCALE_FACTOR，灯光/材质/tone mapping 完全照搬 Viewport3D
   - 支持等距相机（爆炸图、缩图）和正交相机（page 2-5 的 4 个面视图）
   ================================================================ */

import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import type { RenderNode, SideId, Dimensions3, Material } from '../core/model/types';
import { SCALE_FACTOR } from '../core/renderTree';
import { loadTexture, loadNormalMap, hashSeed } from '../core/textures';

export type OrthoLook = 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';

export type ViewSpec =
  | { kind: 'iso' }
  | { kind: 'ortho'; look: OrthoLook };

interface RenderOptions {
  width?: number;
  height?: number;
  bgColor?: number;
  bgAlpha?: number;
  explode?: number;             // mm；>0 启用爆炸视图
  /**
   * 爆炸模式：
   *  - 'side'（默认）：按 sideId 整体平移 → 6 面爆开来（用于第 1 页整箱爆炸图）
   *  - 'layer'：按 layerIndex 在该面厚度方向阶梯式偏移 → 单面内部各层板拆开（用于 page 2-5 单面拆解图）
   */
  explodeMode?: 'side' | 'layer';
  filterSideIds?: SideId[];     // 仅渲染指定面
  view?: ViewSpec;              // 默认 iso
  materialLibrary?: Material[]; // 用于纹理 url 解析
  enableNormalMap?: boolean;    // 启用 normal map（贵，缩图可关）
  /** 产品照感模式：加木台面 + 接触阴影 + 右前 30° 视角 + 不透明白底 */
  productPhotoStyle?: boolean;
}

const DEFAULTS: Required<Pick<RenderOptions, 'width' | 'height' | 'bgColor' | 'bgAlpha' | 'explode'>> = {
  width: 1200,
  height: 800,
  bgColor: 0xfafafa,
  bgAlpha: 0,
  explode: 0,
};

// 各面法向（'side' 模式爆炸方向 + 'layer' 模式相机定位的 +normal 方向）
const SIDE_NORMAL: Record<SideId, [number, number, number]> = {
  pedestal: [0, -1, 0],
  topCap:   [0, 1, 0],
  sideFront:[0, 0, -1],
  sideBack: [0, 0, 1],
  endLeft:  [-1, 0, 0],
  endRight: [1, 0, 0],
};

/**
 * 'layer' 模式（page 2-5 单面拆解图）每个面的相机定制方向
 * 原则：相机大致沿 +SIDE_NORMAL 方向（朝外）+ 30° 抬高 + 30° 横向偏，
 *       让爆炸方向 -SIDE_NORMAL 在屏幕上分离度最大（≈87% 投影）
 * 视觉：每页相机角度不同（不再固定右前上方），但每页本身拆解最清晰
 */
const SIDE_CAMERA_DIR: Record<SideId, [number, number, number]> = {
  pedestal:  [ 1,  0.7,  1],   // 右前上方俯视（已实测完美）
  topCap:    [ 1,  0.7,  1],   // 同 pedestal — 顶盖俯视看见顶板 + 加固
  sideFront: [ 1,  0.5, -1.5], // 右前略偏前方（从前侧外面看进来）
  sideBack:  [ 1,  0.5,  1.5], // 右前略偏后方
  endLeft:   [-1.5, 0.5,  1],  // 左前略偏左方（从左端外面看进来）
  endRight:  [ 1.5, 0.5,  1],  // 右前略偏右方
};

/** 渲染场景到 PNG dataURL */
export async function renderToDataUrl(
  nodes: RenderNode[],
  outerSize: Dimensions3,
  options: RenderOptions = {},
): Promise<string> {
  const opts = { ...DEFAULTS, ...options };
  const W = opts.width;
  const H = opts.height;
  const view = options.view ?? { kind: 'iso' };
  const useNormal = options.enableNormalMap ?? false;
  const productPhoto = options.productPhotoStyle ?? false;

  // 过滤
  const filterSet = options.filterSideIds ? new Set(options.filterSideIds) : null;
  const renderNodes = filterSet ? nodes.filter(n => filterSet.has(n.sideId)) : nodes;

  // 预加载贴图（diffuse + 可选 normal）
  type TexBundle = { diffuse: THREE.Texture; normal: THREE.Texture | null };
  const texBundles = new Map<number, TexBundle>();
  if (options.materialLibrary) {
    const usedKeys = new Set(renderNodes.map(n => n.material));
    await Promise.all(
      Array.from(usedKeys).map(async (key) => {
        const mat = options.materialLibrary!.find(m => m.key === key);
        if (!mat?.textureUrl) return;
        try {
          const diffuse = await loadTexture(mat.textureUrl);
          const normal = useNormal ? await loadNormalMap(mat.textureUrl) : null;
          texBundles.set(key, { diffuse, normal });
        } catch {
          // ignore single-texture failures
        }
      }),
    );
  }

  // 产品照模式：纯白不透明背景；否则按调用方 bgColor/bgAlpha
  const effectiveBgColor = productPhoto ? 0xffffff : opts.bgColor;
  const effectiveBgAlpha = productPhoto ? 1 : opts.bgAlpha;

  // Renderer：与 Viewport3D 同色彩管线
  const renderer = new THREE.WebGLRenderer({
    alpha: effectiveBgAlpha < 1,
    antialias: true,
    preserveDrawingBuffer: true,
  });
  renderer.setSize(W, H, false);
  renderer.setPixelRatio(2);
  renderer.setClearColor(effectiveBgColor, effectiveBgAlpha);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  // 产品照模式提亮箱子（用户反馈"爆炸图太暗，纹理细节差"），白底反弹更多光，需更强曝光
  renderer.toneMappingExposure = productPhoto ? 1.55 : 1.18;

  const scene = new THREE.Scene();

  // Lights：产品照模式 — 强主光 + 适度环境光（高 ambient 会洗掉木纹微阴影 → 纹理变糊）
  scene.add(new THREE.AmbientLight(0xf8f1e5, productPhoto ? 0.65 : 0.85));
  const keyLight = new THREE.DirectionalLight(0xfff4dc, productPhoto ? 2.65 : 1.75);
  keyLight.position.set(5, 8, 4);
  scene.add(keyLight);
  const fillLight = new THREE.DirectionalLight(0xd9e4ee, productPhoto ? 0.65 : 0.55);
  fillLight.position.set(-4, 5, -2);
  scene.add(fillLight);
  const spot = new THREE.SpotLight(0xffffff, 0.65);
  spot.position.set(0, 6.5, 0);
  spot.angle = 0.72;
  spot.penumbra = 0.45;
  scene.add(spot);

  // 用 Group 集中包裹 → 便于渲染前 recenter
  const root = new THREE.Group();
  scene.add(root);
  const transientTextures: THREE.Texture[] = [];
  const transientLineMaterials: LineMaterial[] = [];
  const transientLineGeometries: LineSegmentsGeometry[] = [];

  for (const n of renderNodes) {
    const w = n.geometry.width * SCALE_FACTOR;
    const h = n.geometry.height * SCALE_FACTOR;
    const d = n.geometry.depth * SCALE_FACTOR;
    const geo = new THREE.BoxGeometry(w, h, d);

    const baseColor = new THREE.Color(n.materialcolor || '#d9b87c');
    const seed = hashSeed(n.meshId);
    const bundle = texBundles.get(n.material);

    let materials: THREE.Material[] | THREE.Material;
    if (bundle?.diffuse) {
      const faces = cloneFaceTextures(
        bundle.diffuse, bundle.normal,
        n.geometry.width, n.geometry.height, n.geometry.depth,
        seed,
      );
      transientTextures.push(...faces.flatMap(f => [f.map, f.normal].filter((x): x is THREE.Texture => !!x)));
      materials = faces.map(f => new THREE.MeshPhysicalMaterial({
        color: baseColor,
        map: f.map,
        normalMap: f.normal,
        normalScale: new THREE.Vector2(f.isEndFace ? 0.18 : 0.50, f.isEndFace ? 0.18 : 0.50),
        roughness: f.isEndFace ? 0.92 : 0.83,
        metalness: 0,
        sheen: 0.02,
        sheenRoughness: 0.98,
        envMapIntensity: 0.28,
      }));
    } else {
      materials = new THREE.MeshPhysicalMaterial({
        color: baseColor,
        roughness: 0.85,
        metalness: 0,
        sheen: 0.02,
      });
    }

    const mesh = new THREE.Mesh(geo, materials as THREE.Material | THREE.Material[]);

    let px = n.position.x * SCALE_FACTOR;
    let py = n.position.y * SCALE_FACTOR;
    let pz = n.position.z * SCALE_FACTOR;
    if (opts.explode > 0) {
      const norm = SIDE_NORMAL[n.sideId];
      if (norm) {
        const mode = options.explodeMode ?? 'side';
        // layer 模式：按 layerIndex 阶梯偏移（单面拆解图）
        //   方向取反 — SIDE_NORMAL 朝外（如 pedestal 朝 -y），但 layer 大的板是朝内/朝上的，
        //   所以 layer 越大应该朝内（+normal 反方向）爆得越远，视觉上"叠层向内打开"
        // side 模式：所有同 sideId 节点同距离平移（6 面爆开）
        const sign = mode === 'layer' ? -1 : 1;
        const stepMultiplier = mode === 'layer' ? (n.layerIndex ?? 0) : 1;
        const ex = opts.explode * SCALE_FACTOR * stepMultiplier * sign;
        px += norm[0] * ex;
        py += norm[1] * ex;
        pz += norm[2] * ex;
      }
    }
    mesh.position.set(px, py, pz);
    root.add(mesh);

    // 板边描边（LineSegments2 + LineMaterial，支持加粗 — 解决 LineBasicMaterial WebGL 1px 限制）
    const edges = new THREE.EdgesGeometry(geo);
    const positions = edges.attributes.position.array as Float32Array;
    const lineGeo = new LineSegmentsGeometry();
    lineGeo.setPositions(Array.from(positions));
    const lineMat = new LineMaterial({
      color: 0x000000,
      linewidth: productPhoto ? 3.5 : 1.5,  // 像素单位（worldUnits 默认 false）
      transparent: false,
      depthTest: true,
      resolution: new THREE.Vector2(W, H),
    });
    const line = new LineSegments2(lineGeo, lineMat);
    line.position.copy(mesh.position);
    line.scale.set(1.001, 1.001, 1.001);
    line.computeLineDistances();
    root.add(line);
    transientLineMaterials.push(lineMat);
    transientLineGeometries.push(lineGeo);
  }

  // 居中
  const bbox = new THREE.Box3().setFromObject(root);
  const center = new THREE.Vector3();
  bbox.getCenter(center);
  root.position.sub(center);

  bbox.setFromObject(root);
  const size = new THREE.Vector3();
  bbox.getSize(size);

  // 相机：page 2-5 单面拆解图 (productPhoto + filterSideIds[0]) 时按面定制角度
  // 让爆炸方向 -SIDE_NORMAL 在屏幕投影上分离度最大
  const cameraSide = productPhoto && options.filterSideIds && options.filterSideIds.length === 1
    ? options.filterSideIds[0]
    : null;
  let camera: THREE.OrthographicCamera | THREE.PerspectiveCamera;
  if (view.kind === 'ortho') {
    camera = makeOrthoCamera(view.look, size, W, H);
  } else {
    camera = makeIsoCamera(size, W, H, productPhoto, cameraSide);
  }
  camera.lookAt(0, 0, 0);

  // 让 key light 跟随相机所在象限：相机看哪一面，主光就打那一面
  // 否则 sideFront/endLeft 等相机会让光源跑到主体背后 → 整页发暗
  if (cameraSide) {
    const camPos = camera.position;
    const maxDim = Math.max(size.x, size.y, size.z);
    const lightDist = maxDim * 1.6;
    // 沿相机方向归一化 + 抬高 → 主光始终在相机斜上方
    const len = Math.hypot(camPos.x, camPos.y, camPos.z) || 1;
    keyLight.position.set(
      (camPos.x / len) * lightDist + maxDim * 0.3,
      maxDim * 1.4,
      (camPos.z / len) * lightDist,
    );
    fillLight.position.set(
      -(camPos.x / len) * lightDist * 0.6,
      maxDim * 0.8,
      -(camPos.z / len) * lightDist * 0.6,
    );
  }

  renderer.render(scene, camera);
  const dataUrl = renderer.domElement.toDataURL('image/png');

  // 清理（cache 中的源贴图保留）
  scene.traverse((obj) => {
    const m = obj as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material;
    if (Array.isArray(mat)) mat.forEach(x => x.dispose());
    else if (mat) (mat as THREE.Material).dispose();
  });
  for (const t of transientTextures) t.dispose();
  for (const lm of transientLineMaterials) lm.dispose();
  for (const lg of transientLineGeometries) lg.dispose();
  renderer.dispose();
  void outerSize;

  return dataUrl;
}

function makeOrthoCamera(
  look: OrthoLook,
  size: THREE.Vector3,
  W: number,
  H: number,
): THREE.OrthographicCamera {
  const padding = 1.06;
  let frustumW = 0;
  let frustumH = 0;
  let pos: [number, number, number] = [0, 0, 0];
  let up: [number, number, number] = [0, 1, 0];

  switch (look) {
    case 'top':
      frustumW = size.x; frustumH = size.z;
      pos = [0, 10, 0]; up = [0, 0, -1];
      break;
    case 'bottom':
      frustumW = size.x; frustumH = size.z;
      pos = [0, -10, 0]; up = [0, 0, 1];
      break;
    case 'front':
      frustumW = size.x; frustumH = size.y;
      pos = [0, 0, 10]; up = [0, 1, 0];
      break;
    case 'back':
      frustumW = size.x; frustumH = size.y;
      pos = [0, 0, -10]; up = [0, 1, 0];
      break;
    case 'left':
      frustumW = size.z; frustumH = size.y;
      pos = [-10, 0, 0]; up = [0, 1, 0];
      break;
    case 'right':
      frustumW = size.z; frustumH = size.y;
      pos = [10, 0, 0]; up = [0, 1, 0];
      break;
  }
  frustumW *= padding;
  frustumH *= padding;

  // 让 frustum 适配画布纵横比
  const targetAspect = W / H;
  const sceneAspect = frustumW / frustumH;
  if (sceneAspect > targetAspect) {
    frustumH = frustumW / targetAspect;
  } else {
    frustumW = frustumH * targetAspect;
  }

  const cam = new THREE.OrthographicCamera(
    -frustumW / 2, frustumW / 2,
    frustumH / 2, -frustumH / 2,
    0.001, 200,
  );
  cam.position.set(...pos);
  cam.up.set(...up);
  return cam;
}

function makeIsoCamera(
  size: THREE.Vector3,
  W: number,
  H: number,
  productPhoto = false,
  cameraSide: SideId | null = null,
): THREE.PerspectiveCamera {
  const fov = 38;
  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  // distMul：相机距离 = 物体最大维度 × 此倍数。越小 → 物体在画面中越大
  // reference 实测：box 在 area 中约占 50-60%，上下左右 15-20% 边距 → distMul ≈ 1.95
  // 之前 1.40 物体几乎贴边（用户反馈"超出界面"），1.65 仍偏挤，1.95 与 reference 视觉一致
  const distMul = productPhoto ? 1.95 : 1.95;
  const camDist = (maxDim / 2) / Math.tan((fov / 2) * Math.PI / 180) * distMul;
  const cam = new THREE.PerspectiveCamera(fov, W / H, maxDim * 0.05, maxDim * 20);
  const d = camDist / Math.sqrt(3);

  // 优先级 1: page 2-5 单面拆解图 → 该面的定制角度（让爆炸方向 -SIDE_NORMAL 屏幕分离最大）
  if (cameraSide && SIDE_CAMERA_DIR[cameraSide]) {
    const dir = SIDE_CAMERA_DIR[cameraSide];
    // dir 不一定是单位向量，按其长度比例缩放到 d
    const len = Math.hypot(dir[0], dir[1], dir[2]);
    const k = d / len;
    cam.position.set(dir[0] * k * Math.sqrt(3), dir[1] * k * Math.sqrt(3), dir[2] * k * Math.sqrt(3));
    return cam;
  }
  // 优先级 2: 产品照模式（page 1 组装/分解图）
  if (productPhoto) {
    cam.position.set(d, d * 0.7, d);
    return cam;
  }
  // 优先级 3: 默认 iso（其他用途）
  cam.position.set(d, d * 0.85, -d);
  return cam;
}

/** 端口自 Viewport3D.cloneFaceTextures：6 面分别配置（rotate + seeded offset）*/
function cloneFaceTextures(
  diffuse: THREE.Texture,
  normal: THREE.Texture | null,
  widthMm: number,
  heightMm: number,
  depthMm: number,
  seed: number,
): Array<{ map: THREE.Texture; normal: THREE.Texture | null; isEndFace: boolean }> {
  const majorAxis: 'x' | 'y' | 'z' =
    widthMm >= heightMm && widthMm >= depthMm
      ? 'x'
      : heightMm >= depthMm
        ? 'y'
        : 'z';

  // BoxGeometry material order: +X, -X, +Y, -Y, +Z, -Z
  const faceConfigs = [
    { normalAxis: 'x' as const, rotate: majorAxis === 'y' },
    { normalAxis: 'x' as const, rotate: majorAxis === 'y' },
    { normalAxis: 'y' as const, rotate: majorAxis === 'z' },
    { normalAxis: 'y' as const, rotate: majorAxis === 'z' },
    { normalAxis: 'z' as const, rotate: majorAxis === 'y' },
    { normalAxis: 'z' as const, rotate: majorAxis === 'y' },
  ];

  const offsetU = seed;
  const offsetV = (seed * 0.6180339) % 1;

  const configure = (
    tex: THREE.Texture,
    config: typeof faceConfigs[number],
    isNormal: boolean,
  ): THREE.Texture => {
    tex.needsUpdate = true;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = isNormal ? THREE.NoColorSpace : THREE.SRGBColorSpace;
    // 各向异性过滤：木纹在斜面上不会被双线性过滤糊成一片（Three.js 会按设备能力 clamp）
    tex.anisotropy = 16;
    tex.repeat.set(1, 1);
    tex.offset.set(offsetU * 0.08, offsetV * 0.08);
    if (config.rotate) {
      tex.center.set(0.5, 0.5);
      tex.rotation = Math.PI / 2;
    }
    return tex;
  };

  return faceConfigs.map(config => {
    // 端面（短轴方向的两个面）— 用于材质 roughness 区分
    // 与 majorAxis 相同方向的面 = 端面
    const isEndFace = config.normalAxis === majorAxis;
    return {
      map: configure(diffuse.clone(), config, false),
      normal: normal ? configure(normal.clone(), config, true) : null,
      isEndFace,
    };
  });
}
